import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import HealthChecksList from '../components/HealthChecksList';
import type { AppAccessSummary, AppSanityReport, Conversation } from '../types';
import { canManageMembers, canManageProject, effectiveAccessSummary } from '../utils/projectAccess';
import { reportErrorLocal } from '../utils/errors';
import AppDetailPage from './AppDetailPage';
import AppMembersPage from './AppMembersPage';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  page: 'overview' | 'members';
  canUseLegacyAppManagement: boolean;
};

/** Route params may change before React runs the access-refresh effect. */
export function accessMatchesCurrentApp(
  access: AppAccessSummary | null | undefined,
  appId: string
): access is AppAccessSummary {
  return Boolean(access && access.app_id === appId);
}

/** Do not render an app health response after navigation unless it is for this route. */
export function healthReportMatchesCurrentApp(
  report: AppSanityReport | null | undefined,
  appId: string
): report is AppSanityReport {
  return Boolean(report && report.app_id === appId);
}

export default function AppAccessRoute({
  apiClient,
  setError,
  page,
  canUseLegacyAppManagement
}: Props) {
  const appId = useParams().appId ?? '';
  const [access, setAccess] = useState<AppAccessSummary | null>(null);
  const [pageError, setPageError] = useState<{ appId: string; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Route components are reused when only `:appId` changes. Clear the prior
    // project's authorization before starting the next lookup so a slow or
    // failed request cannot briefly expose controls based on stale access.
    // Do not let a response for the former route restore its access after a
    // navigation to a different project.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAccess(null);
    setPageError(null);
    apiClient.get<AppAccessSummary>(`/api/apps/${appId}/access`).then((result) => {
      if (cancelled) return;
      setAccess(result);
    }).catch((err) => {
      if (!cancelled) {
        reportErrorLocal('Failed to load project access', err, setError, (message) => {
          setPageError(message ? { appId, message } : null);
        });
      }
    });
    return () => { cancelled = true; };
  }, [apiClient, appId, setError]);
  if (pageError?.appId === appId) return <ErrorBanner message={pageError.message} onDismiss={() => setPageError(null)} />;
  // Effects run after a route render. Treat a summary for a different app as
  // absent so navigation never paints controls or project data from the prior
  // route while the next authorization request is starting.
  if (!accessMatchesCurrentApp(access, appId)) return <div className="card">Loading project access…</div>;
  if (!access.role && !access.admin_bypass) return <div className="card"><h2>Project access required</h2><p>You do not have access to this project.</p></div>;

  if (page === 'members') {
    return canManageMembers(access)
      ? <AppMembersPage apiClient={apiClient} setError={setError} access={access} />
      : <Navigate to={`/apps/${appId}`} replace />;
  }
  const pageContent = canUseLegacyAppManagement && canManageProject(access)
    ? <AppDetailPage apiClient={apiClient} setError={setError} showMembersLink={canManageMembers(access)} />
    : <AppProjectOverview apiClient={apiClient} setError={setError} access={access} />;

  return <>
    {canManageProject(access) && (
      <AppHealthPanel
        apiClient={apiClient}
        setError={setError}
        appId={appId}
        appName={access.project_name ?? access.project_slug ?? 'Application'}
      />
    )}
    {pageContent}
  </>;
}

function AppHealthPanel({
  apiClient,
  setError,
  appId,
  appName
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  appId: string;
  appName: string;
}) {
  const [report, setReport] = useState<AppSanityReport | null>(null);
  const [healthError, setHealthError] = useState<{ appId: string; message: string } | null>(null);
  const healthLoadGenerationRef = useRef(0);

  const loadHealth = useCallback(async () => {
    const generation = healthLoadGenerationRef.current + 1;
    healthLoadGenerationRef.current = generation;
    try {
      const nextReport = await apiClient.get<AppSanityReport>(`/api/apps/${appId}/health`);
      if (healthLoadGenerationRef.current === generation) {
        setReport(nextReport);
        setHealthError(null);
      }
    } catch (err) {
      if (healthLoadGenerationRef.current === generation) {
        setReport(null);
        reportErrorLocal(
          'Failed to load application health',
          err,
          setError,
          (message) => setHealthError(message ? { appId, message } : null)
        );
      }
    }
  }, [apiClient, appId, setError]);

  useEffect(() => {
    // Route components are reused when only `:appId` changes. The generation
    // guard ensures a response for the previous app cannot restore stale data.
    // State updates occur only after the asynchronous request settles.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadHealth();
    return () => {
      healthLoadGenerationRef.current += 1;
    };
  }, [loadHealth]);

  const currentReport = healthReportMatchesCurrentApp(report, appId) ? report : null;
  const currentHealthError = healthError?.appId === appId ? healthError.message : null;
  if (!currentHealthError && (!currentReport || currentReport.checks.length === 0)) {
    return null;
  }

  return <section className="grid project-health-panel">
    <div className="card full">
      <div className="card-header">
        <div>
          <h2>Application health</h2>
          <p className="status">Configuration issues for {appName}</p>
        </div>
        <button type="button" onClick={() => void loadHealth()}>Refresh</button>
      </div>
      {currentHealthError && <ErrorBanner message={currentHealthError} onDismiss={() => setHealthError(null)} />}
      {currentReport && currentReport.checks.length > 0 && <HealthChecksList checks={currentReport.checks} />}
    </div>
  </section>;
}

function AppProjectOverview({ apiClient, setError, access }: { apiClient: ApiClient; setError: (message: string | null) => void; access: AppAccessSummary }) {
  const appId = useParams().appId ?? '';
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<Conversation[]>(`/api/apps/${appId}/conversations`).then((nextConversations) => {
      if (cancelled) return;
      setConversations(nextConversations);
      setLoaded(true);
    }).catch((err) => {
      if (!cancelled) reportErrorLocal('Failed to load project overview', err, setError, setPageError);
    });
    return () => { cancelled = true; };
  }, [apiClient, appId, setError]);

  if (pageError) return <ErrorBanner message={pageError} onDismiss={() => setPageError(null)} />;
  if (!loaded) return <div className="card">Loading project overview…</div>;
  return <section className="grid project-overview-page">
    <div className="card full">
      <div className="card-header"><div><h2>{access.project_name ?? access.project_slug ?? 'Project'}</h2><p className="status">Project overview</p></div>{canManageMembers(access) && <Link className="link-button" to={`/apps/${appId}/members`}>Members</Link>}</div>
      <dl className="details-list">
        <div><dt>Project role</dt><dd><span className="badge">{access.role ?? 'Platform administrator'}</span></dd></div>
        <div><dt>Effective access</dt><dd>{effectiveAccessSummary(access.role ?? 'administrator', access.capabilities)}</dd></div>
        <div><dt>GitLab</dt><dd className="mono">{access.gitlab_project_path ?? access.gitlab_url ?? 'Not configured'}</dd></div>
      </dl>
      <p className="hint">This limited view intentionally does not load secrets, operations, deployment, or runtime-management data.</p>
    </div>
    <div className="card full"><div className="card-header"><h2>Project conversations</h2><Link className="link-button" to="/conversations">All conversations</Link></div>
      <table className="responsive-table"><thead><tr><th>Title</th><th>Status</th><th>Updated</th></tr></thead><tbody>
      {conversations.map((conversation) => <tr key={conversation.id}><td><Link to={`/apps/${appId}/conversations/${conversation.id}`}>{conversation.title ?? 'Untitled'}</Link></td><td><span className="badge">{conversation.status}</span></td><td>{new Date(conversation.updated_at).toLocaleString()}</td></tr>)}
      {conversations.length === 0 && <tr><td colSpan={3}>No permitted conversations yet.</td></tr>}
      </tbody></table>
    </div>
  </section>;
}
